import type {RevaLanguage} from "../i18n";
import {formatArea, formatPrice, formatTypedValue, formatValue, parseGuiType} from "../number-format";

/**
 * Fields rendered in the "Sonstige Rechte, Lasten und Beschränkungen" section, shared between the
 * full valuation PDF, the simple valuation PDF (#19004) and the client's register appreciation inputs.
 */
export const ADDITIONAL_LEGAL_RIGHTS_FIELDS = [
    {key: "baulasten", label: "Baulasten"},
    {key: "altlasten", label: "Altlasten"},
    {key: "sonstige", label: "Sonstige Wertbeeinflussungen"},
    {key: "zuwegung", label: "Zuwegung"},
    {key: "baurecht", label: "Baurecht"},
];

/** Grundbuch Abteilung I asset type denoting sole/full ownership (as opposed to a co-ownership share). */
export const FULL_OWNERSHIP_ASSET_TYPE = "Volleigentum";

/** Whether any of the given register assets is a partial-ownership share (i.e. not "Volleigentum"). */
export function hasPartialOwnershipAsset(
    assets: Array<Record<string, unknown>> | null | undefined,
    typeField: string = "register_asset_type",
): boolean {
    return !!assets?.some((asset) => asset?.[typeField] !== FULL_OWNERSHIP_ASSET_TYPE);
}

/** Format override used to display an ownership share percentage (e.g. Miteigentumsanteil). */
export const OWNERSHIP_SHARE_FORMAT = "DOUBLE(0-4)";

/** The plot area attributable to an ownership share (share is a percentage, 0-100). */
export function computeOwnershipShareArea(
    totalSize: number | null | undefined,
    sharePercent: number | null | undefined,
): number | null {
    if (totalSize == null || sharePercent == null) return null;
    return (totalSize * sharePercent) / 100;
}

/** Miteigentumsanteil als „33,3333 % (110,00 m²)“; die Fläche entfällt ohne Grundstücksfläche. */
export function formatOwnershipShare(
    sharePercent: number,
    totalSize: number | null | undefined,
    language: RevaLanguage,
): string {
    const share = formatTypedValue(language, sharePercent, parseGuiType(OWNERSHIP_SHARE_FORMAT, "%", "%"));
    const area = formatArea(computeOwnershipShareArea(totalSize, sharePercent), language);
    return area ? `${share} (${area})` : share;
}

const PARTIAL_OWNERSHIP_FIELDS = ["register_asset_shares", "register_asset_stock"];

/** Ausgabe-Spalten des Bestandsverzeichnisses: Anteile nur, wenn nicht alles Volleigentum ist. */
export function assetOutputColumns<T extends {parameter: string}>(
    columns: T[],
    assets: Array<Record<string, unknown>> | null | undefined,
): T[] {
    return hasPartialOwnershipAsset(assets)
        ? columns
        : columns.filter((column) => !PARTIAL_OWNERSHIP_FIELDS.includes(column.parameter));
}

/**
 * Ausgabe der Zahlenfelder eines Bestandseintrags, undefined für andere Felder oder fehlende Werte.
 * Anteile mit 1–3 Nachkommastellen (#18295).
 */
export function formatAssetValue(parameter: string, value: unknown, language: RevaLanguage): string | undefined {
    if (typeof value !== "number") return undefined;
    switch (parameter) {
        case "register_asset_shares":
            return new Intl.NumberFormat(language, {minimumFractionDigits: 1, maximumFractionDigits: 3}).format(value);
        case "register_asset_stock":
            return formatValue(value, language, true);
        case "register_asset_size":
            return formatArea(value, language);
        default:
            return undefined;
    }
}

/** Sum of Abteilung II (register_abt2) entry values, treating missing values as 0. */
export function sumSectionIIEntryValues(
    entries: Array<Record<string, number | null | undefined>> | null | undefined,
    valueField: string = "section_ii_entry_value",
): number {
    return (entries ?? []).reduce((acc, entry) => acc + (entry?.[valueField] ?? 0), 0);
}

/** register_abt2-Subtyp der vom AVM abgeleiteten Erbbauzins-Zeile. */
export const LEASEHOLD_ENTRY_NAME = "Erbbauzinsreallast";
/** register_abt2-Subtyp der vom AVM abgeleiteten Vorkaufsrecht-Zeile. */
export const PREEMPTION_ENTRY_NAME = "Vorkaufsrecht (§§ 1094 ff. BGB)";

/**
 * Ob eine register_abt2-Zeile manuell erfasst ist, also nicht die vom AVM abgeleitete Erbbau-/Vorkaufsrecht-Zeile.
 * Die abgeleiteten Zeilen tragen von den section_ii_entry_*-Feldern nur Art, Unterart und Betrag; ein Eintrag mit
 * derselben Unterart, aber eigenem Prozentsatz, eigener Basis, Kategorie oder BelW-Angabe ist manuell (#19602).
 */
export function isManualSectionIIEntry(entry: Record<string, any>): boolean {
    const subtype = entry.section_ii_entry_subtype_de;
    if (subtype !== LEASEHOLD_ENTRY_NAME && subtype !== PREEMPTION_ENTRY_NAME) return true;
    return SECTION_II_ENTRY_PARAMS.some((field) => !AUTO_ENTRY_FIELDS.includes(field) && entry[field] != null);
}

/** Kategorie eines Abt.-II-Eintrags (`category`, `category_lending`) in der AVM. */
export const SectionIICategory = {
    VALUE_REDUCING: 1,
    PRIOR_CHARGE: 2,
    NOT_CONSIDERED: 3,
} as const;

/** Ein Eintrag aus `section_ii_entries` der Indikation. */
export interface SectionIIEntryResult {
    type_de?: string | null;
    subtype_de?: string | null;
    value?: number | null;
    category?: number | null;
    percentage?: number | null;
    base?: number | null;
    value_lending?: number | null;
    category_lending?: number | null;
    percentage_lending?: number | null;
    base_lending?: number | null;
}

/**
 * Felder des Marktwerts und Beleihungswerts eines Abt.-II-Eintrags in register_abt2 und in `section_ii_entries`.
 * `defaultCategory` entspricht dem Default der AVM ohne Angabe.
 */
export const SECTION_II_VALUE_FIELDS = {
    market: {
        value: "section_ii_entry_value",
        percentage: "section_ii_entry_percentage",
        base: "section_ii_entry_base",
        category: "section_ii_entry_category",
        defaultCategory: SectionIICategory.VALUE_REDUCING,
        resultValue: "value",
        resultCategory: "category",
    },
    lending: {
        value: "section_ii_entry_value_lending",
        percentage: "section_ii_entry_percentage_lending",
        base: "section_ii_entry_base_lending",
        category: "section_ii_entry_category_lending",
        defaultCategory: SectionIICategory.NOT_CONSIDERED,
        resultValue: "value_lending",
        resultCategory: "category_lending",
    },
} as const;

/** Die section_ii_entry_*-Arrays, die REVA je manuellem Abt.-II-Eintrag an die AVM schickt. */
export const SECTION_II_ENTRY_PARAMS: string[] = [
    "section_ii_entry_type_de",
    "section_ii_entry_subtype_de",
    ...Object.values(SECTION_II_VALUE_FIELDS).flatMap(({value, percentage, base, category}) => [
        value,
        percentage,
        base,
        category,
    ]),
];

/** section_ii_entry_*-Felder der vom AVM abgeleiteten Erbbau-/Vorkaufsrecht-Zeile, s. {@link isManualSectionIIEntry}. */
const AUTO_ENTRY_FIELDS: readonly string[] = [
    "section_ii_entry_type_de",
    "section_ii_entry_subtype_de",
    SECTION_II_VALUE_FIELDS.market.value,
];

/**
 * Eine Zeile je Eintrag mit allen gesetzten section_ii_entry_*-Werten einer Schicht. Trägt ein Eintrag die
 * Unterart einer abgeleiteten Zeile, bekommt er die Default-Kategorien der AVM, damit er auch mit reinem Betrag
 * manuell bleibt (#19602).
 */
export function sectionIIRowsFromEntryParams(params: object | null | undefined): Array<Record<string, unknown>> {
    const arrays = SECTION_II_ENTRY_PARAMS.flatMap((field) => {
        const values = (params as Record<string, unknown> | null | undefined)?.[field];
        return Array.isArray(values) ? [[field, values] as const] : [];
    });
    const length = Math.max(0, ...arrays.map(([, values]) => values.length));
    return Array.from({length}, (_, index) => {
        const row: Record<string, unknown> = Object.fromEntries(
            arrays.flatMap(([field, values]) => (values[index] != null ? [[field, values[index]]] : [])),
        );
        if (isManualSectionIIEntry(row)) return row;
        return {
            ...Object.fromEntries(
                Object.values(SECTION_II_VALUE_FIELDS).map(({category, defaultCategory}) => [
                    category,
                    defaultCategory,
                ]),
            ),
            ...row,
        };
    });
}

/** Ob ein Eintrag aus `section_ii_entries` in die Summe des Marktwerts bzw. Beleihungswerts eingeht. */
export function countsToSectionIISum(line: keyof typeof SECTION_II_VALUE_FIELDS, entry: SectionIIEntryResult): boolean {
    const {resultCategory, defaultCategory} = SECTION_II_VALUE_FIELDS[line];
    return (entry[resultCategory] ?? defaultCategory) === SectionIICategory.VALUE_REDUCING;
}

/** Ausgabe eines Abt.-II-Betrags: berechnet vor eingegeben, „–“ für nicht berücksichtigt, eine Vorlast in Klammern. */
export function formatSectionIIOutputValue(
    line: keyof typeof SECTION_II_VALUE_FIELDS,
    row: Record<string, any>,
    result: SectionIIEntryResult | undefined,
    language: RevaLanguage,
): string | null {
    const fields = SECTION_II_VALUE_FIELDS[line];
    const category = result?.[fields.resultCategory] ?? row[fields.category] ?? fields.defaultCategory;
    if (category === SectionIICategory.NOT_CONSIDERED) return "–";
    const value = result?.[fields.resultValue] ?? row[fields.value];
    if (typeof value !== "number") return null;
    const formatted = formatPrice(value, language);
    return category === SectionIICategory.PRIOR_CHARGE ? `(${formatted})` : formatted;
}

const SECTION_II_COLUMN_ORDER = [
    "register_abt2_parcel",
    "register_abt2_nr",
    "section_ii_entry_type_de",
    "section_ii_entry_subtype_de",
    "section_ii_entry_value",
];

/**
 * Abt.-II-Spalten der Ausgabe: `main` ohne Textspalten in der Reihenfolge der Eingabe (unbekannte dahinter),
 * `text` die Unterart und die Textspalten.
 */
export function splitSectionIIColumns<T extends {parameter: string; details?: {type?: string | null} | null}>(
    columns: T[],
): {main: T[]; text: T[]} {
    const isText = (column: T) => column.details?.type === "text";
    const rank = (parameter: string) => {
        const index = SECTION_II_COLUMN_ORDER.indexOf(parameter);
        return index < 0 ? SECTION_II_COLUMN_ORDER.length : index;
    };
    return {
        main: columns.filter((column) => !isText(column)).sort((a, b) => rank(a.parameter) - rank(b.parameter)),
        text: columns.filter((column) => column.parameter === "section_ii_entry_subtype_de" || isText(column)),
    };
}

/**
 * Ordnet jeder Zeile aus {@link withLiveLeaseholdEntries} ihr Ergebnis aus `section_ii_entries` zu. Die AVM
 * liefert die Einträge in derselben Reihenfolge (erst die manuellen, dann Erbbau und Vorkaufsrecht); passt die
 * Unterart nicht, bleibt die Zeile ohne Ergebnis.
 */
export function matchSectionIIResults(
    rows: Array<Record<string, any>>,
    results: SectionIIEntryResult[] | null | undefined,
): Array<SectionIIEntryResult | undefined> {
    return rows.map((row, index) => {
        const result = results?.[index];
        return result && (result.subtype_de ?? null) === (row.section_ii_entry_subtype_de ?? null) ? result : undefined;
    });
}

/**
 * Überschreibt den Betrag der Erbbau-/Vorkaufsrecht-Zeile in register_abt2 bei jedem Lesen live mit
 * discount_leasehold_amount/discount_preemption_right der führenden Indikation, statt ihn zu
 * persistieren (#19379). Beide Zeilen werden dabei immer ans Ende gestellt
 */
export function withLiveLeaseholdEntries(
    entries: Array<Record<string, any>> | null | undefined,
    leadingIndicationDetails:
        | {discount_leasehold_amount?: number | null; discount_preemption_right?: number | null}
        | null
        | undefined,
): Array<Record<string, any>> {
    const leasehold = leadingIndicationDetails?.discount_leasehold_amount;
    const preemption = leadingIndicationDetails?.discount_preemption_right;

    const manualEntries = (entries ?? []).filter(isManualSectionIIEntry);
    const autoEntries = (entries ?? []).filter((entry) => !isManualSectionIIEntry(entry));
    const existingLeasehold = autoEntries.find((entry) => entry.section_ii_entry_subtype_de === LEASEHOLD_ENTRY_NAME);
    const existingPreemption = autoEntries.find((entry) => entry.section_ii_entry_subtype_de === PREEMPTION_ENTRY_NAME);

    const result = [...manualEntries];
    if (leasehold != null) {
        result.push(
            existingLeasehold
                ? {...existingLeasehold, section_ii_entry_value: leasehold}
                : {
                      section_ii_entry_type_de: "Reallast",
                      section_ii_entry_subtype_de: LEASEHOLD_ENTRY_NAME,
                      section_ii_entry_value: leasehold,
                  },
        );
    }
    if (preemption) {
        result.push(
            existingPreemption
                ? {...existingPreemption, section_ii_entry_value: preemption}
                : {
                      section_ii_entry_type_de: "Vormerkung",
                      section_ii_entry_subtype_de: PREEMPTION_ENTRY_NAME,
                      section_ii_entry_value: preemption,
                  },
        );
    }
    return result;
}
